/**
 * CONFIGURACIÓN DE BRANDING DINÁMICO PARA SSO
 * 
 * Define la identidad visual completa (colores, logos, textos) para cada app.
 * Los colores están en formato HSL sin comas (ej: "210 100% 50%") para compatibilidad con Tailwind CSS.
 * 
 * @package @shared/config
 */

export interface BrandingConfig {
  appName: string;
  label: string;
  colors: {
    primary: string;              // HSL: "210 100% 50%"
    primaryForeground: string;
    background: string;
    foreground: string;
    card?: string;
    cardForeground?: string;
    secondary?: string;
    secondaryForeground?: string;
    muted?: string;
    mutedForeground?: string;
    border?: string;
    input?: string;
    ring?: string;
  };
  assets: {
    logo: string;
    logoDark?: string;
    favicon?: string;
    heroImage?: string;           // Imagen para split screen layout
    logoStyle?: 'horizontal' | 'vertical' | 'square'; // Estilo del logo
  };
  copy: {
    title: string;
    subtitle: string;
    buttonText?: string;
  };
  layout?: {
    splitScreen?: boolean;        // Activar layout de dos columnas
  };
  // 🎬 SPLASH SCREEN - Palabras animadas mientras carga
  splash?: {
    words: string[];              // Palabras que rotan en el splash (ej: ['Conectando', 'Seguro', 'Inteligente'])
  };
  // 🌐 LANDING BACKGROUND - URL de la landing page para fondo
  landingUrl?: {
    dev?: string;                 // URL en desarrollo (ej: "http://localhost:5177")
    prod: string;                 // URL en producción (ej: "https://houseme.app")
  };
  // 🎨 ADN DE DISEÑO - Valores reales extraídos de cada app
  design: {
    // Tipografía
    fontFamily: string;           // Font-family real (ej: "'Inter', system-ui, sans-serif")
    fontWeights: {
      normal: number;             // 400
      medium: number;             // 500
      semibold: number;           // 600
      bold: number;               // 700
    };
    // Border radius
    radius: string;               // Valor real (ej: "8px", "0.5rem")
    // Sombras
    shadows: {
      sm: string;                  // box-shadow pequeño
      md: string;                  // box-shadow medio
      lg: string;                  // box-shadow grande
    };
    // Google Fonts (si aplica)
    googleFonts?: {
      families: string[];         // ['Inter:400,500,600', 'Figtree:400,500,600']
      display?: string;           // 'swap' | 'block' | 'fallback'
    };
  };
}

/**
 * Configuración de branding por app
 * 
 * NOTA: Los colores están en formato HSL sin comas para que Tailwind los procese correctamente.
 * Ejemplo: "210 100% 50%" se usa como hsl(var(--primary)) en Tailwind.
 */
export const BRANDING_CONFIGS: Record<string, BrandingConfig> = {
  empresa: {
    appName: 'empresa',
    label: 'Empresa',
    colors: {
      // 🎨 TEMA CLARO PROFESIONAL - Empresa (modo claro por defecto)
      primary: '222 47% 11%',              // #0f172a (slate-900) - Mantener color primario oscuro para botones
      primaryForeground: '0 0% 100%',      // #ffffff (blanco)
      background: '0 0% 100%',            // ✅ Fondo blanco (modo claro)
      foreground: '222 47% 11%',          // ✅ Texto oscuro para legibilidad
      card: '210 20% 98%',                // ✅ Cards claras (similar a houseme)
      cardForeground: '222 47% 11%',      // ✅ Texto oscuro en cards
      secondary: '222 47% 15%',           // #1e293b (slate-800)
      secondaryForeground: '0 0% 100%',    // #ffffff
      muted: '210 20% 96%',               // ✅ Gris claro para fondos
      mutedForeground: '215 16% 38%',     // ✅ Texto secundario oscuro para legibilidad
      border: '0 0% 0% / 0.08',            // ✅ Borde visible en modo claro
      input: '0 0% 0% / 0.08',             // ✅ Border de inputs visible
      ring: '217 91% 60%',                 // #3b82f6 - Azul brillante para focus
    },
    assets: {
      logo: '/icon-empresa-192x192.png',  // ✅ Icono específico de Empresa en accounts
      logoDark: '/icon-empresa-192x192.png', // ✅ Mismo icono (modo claro)
      favicon: '/favicon.ico',
      heroImage: '/assets/images/bg-auth.jpg', // Imagen para split screen
      logoStyle: 'square',                 // ✅ Icono cuadrado (como houseme)
    },
    copy: {
      title: 'Bienvenido a ThePlatform360',
      subtitle: 'Sistema de gestión empresarial completo',
      buttonText: 'Acceder',
    },
    layout: {
      splitScreen: true,                   // Activar layout de dos columnas
    },
    splash: {
      words: ['Cargando', 'Profesional', 'Eficiente', 'Gestión', 'Completo', 'Seguro'],
    },
    landingUrl: {
      dev: 'http://localhost:5173/empresa',
      prod: 'https://www.theplatform360.app',
    },
    design: {
      // 🎨 ADN REAL DE EMPRESA - Extraído de tailwind.config.js y index.html
      fontFamily: "'Figtree', 'sans-serif'", // Font principal según tailwind.config.js
      fontWeights: {
        normal: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
      },
      radius: '0.5rem',                    // 8px - Valor real de shared/index.scss
      shadows: {
        sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',      // Sombra pequeña estándar
        md: '0 2px 4px -1px rgba(0, 0, 0, 0.06)',   // Sombra media estándar
        lg: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',    // Sombra grande estándar
      },
      googleFonts: {
        families: ['Inter:wght@400;500;600', 'Manrope:wght@600;700'], // Fonts cargados en index.html
        display: 'swap',
      },
    },
  },
  tienda: {
    appName: 'tienda',
    label: 'Tienda',
    colors: {
      primary: '0 84% 26%',                // #7c2d12 (red-900)
      primaryForeground: '0 0% 100%',      // #ffffff
      background: '0 0% 100%',              // #ffffff
      foreground: '0 0% 22%',              // #37352F
      card: '0 0% 97%',                    // #F7F6F3
      cardForeground: '0 0% 22%',          // #37352F
      secondary: '0 70% 34%',              // #991b1b (red-800)
      secondaryForeground: '0 0% 100%',    // #ffffff
      muted: '0 0% 97%',                   // #F7F6F3
      mutedForeground: '0 0% 47%',        // #787774
      border: '0 0% 91%',                  // #E9E9E7
      input: '0 0% 91%',                   // #E9E9E7
      ring: '0 84% 26%',                   // Mismo que primary
    },
    assets: {
      logo: '/branding/tienda/logo.svg',
      logoDark: '/branding/tienda/logo-dark.svg',
    },
    copy: {
      title: 'Iniciar sesión en Tienda',
      subtitle: 'Gestión de catálogo y ventas',
      buttonText: 'Iniciar sesión',
    },
    splash: {
      words: ['Preparando', 'Catálogo', 'Ventas', 'Rápido', 'Intuitivo', 'Completo'],
    },
    landingUrl: {
      dev: 'http://localhost:5174',
      prod: 'https://tienda.elfom.com',
    },
    design: {
      // 🎨 ADN POR DEFECTO - Tienda usa valores estándar
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      fontWeights: {
        normal: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
      },
      radius: '0.5rem',                    // 8px - Valor estándar
      shadows: {
        sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        md: '0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        lg: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      },
      googleFonts: {
        families: ['Inter:wght@400;500;600'],
        display: 'swap',
      },
    },
  },
  houseme: {
    appName: 'houseme',
    label: 'HouseMe',
    colors: {
      // 🎨 TEMA CLARO Y AMIGABLE - HouseMe (valores optimizados para contraste)
      primary: '89 36% 40%',               // #748D44 - Verde oliva
      primaryForeground: '0 0% 100%',      // #ffffff - Blanco para botones
      background: '0 0% 100%',             // #FFFFFF - Fondo blanco sólido
      foreground: '217 33% 15%',           // ✅ Texto más oscuro para mejor contraste
      card: '210 20% 98%',                 // ✅ Gris muy claro para contraste sobre fondo blanco
      cardForeground: '217 33% 15%',       // ✅ Texto oscuro para legibilidad
      secondary: '182 35% 53%',            // #589DA4 - Azul turquesa
      secondaryForeground: '0 0% 100%',    // #ffffff
      muted: '210 20% 96%',                // Gris muy claro para fondos
      mutedForeground: '215 16% 38%',      // ✅ Gris más oscuro para mejor legibilidad
      border: '0 0% 0% / 0.08',            // ✅ Borde ligeramente más visible
      input: '0 0% 0% / 0.08',             // ✅ Border de inputs más visible
      ring: '89 36% 40%',                  // Verde oliva para focus
    },
    assets: {
      logo: '/icon-192x192.png',          // Icono PWA HouseMe (existe en houseme/public)
      logoStyle: 'square',                // ✅ Icono cuadrado
    },
    copy: {
      title: '¡Hola! Bienvenido',
      subtitle: 'Tu hogar inteligente te espera',
      buttonText: 'Entrar',
    },
    layout: {
      splitScreen: true,                   // Activar layout de dos columnas
    },
    splash: {
      words: ['Conectando', 'Seguro', 'Inteligente', 'Tu hogar', 'Moderno', 'Simple'],
    },
    landingUrl: {
      dev: 'http://localhost:5177',
      prod: 'https://houseme.app',
    },
    design: {
      // 🎨 ADN REAL DE HOUSEME - Extraído de design-tokens.css y tailwind.config.js
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif", // Valor real de design-tokens.css
      fontWeights: {
        normal: 400,                       // Valor real
        medium: 500,                      // Valor real
        semibold: 600,                    // Valor real
        bold: 700,                        // Valor real
      },
      radius: '8px',                      // --houseme-radius-md (valor real)
      shadows: {
        sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',      // --houseme-shadow-sm (valor real)
        md: '0 2px 4px -1px rgba(0, 0, 0, 0.06)',   // --houseme-shadow-md (valor real)
        lg: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',    // --houseme-shadow-lg (valor real)
      },
      // Houseme NO carga Google Fonts en index.html, usa Inter del sistema
      googleFonts: undefined,
    },
  },
  thefomlabs: {
    appName: 'thefomlabs',
    label: 'THEFOMLABS',
    colors: {
      primary: '222 47% 11%',
      primaryForeground: '0 0% 100%',
      background: '0 0% 100%',
      foreground: '222 47% 11%',
      card: '210 20% 98%',
      cardForeground: '222 47% 11%',
      secondary: '222 47% 15%',
      secondaryForeground: '0 0% 100%',
      muted: '210 20% 96%',
      mutedForeground: '215 16% 38%',
      border: '0 0% 0% / 0.08',
      input: '0 0% 0% / 0.08',
      ring: '222 47% 11%',
    },
    assets: {
      logo: '/icon-thefomlabs-192x192.png',
      logoDark: '/icon-thefomlabs-192x192.png',
      favicon: '/web/favicon.ico',
      logoStyle: 'square',
    },
    copy: {
      title: 'THEFOMLABS',
      subtitle: 'Identidad central del ecosistema',
      buttonText: 'Entrar',
    },
    splash: {
      words: ['Cargando', 'Innovación', 'Ecosistema', 'ThePlatform360', 'Seguro'],
    },
    landingUrl: {
      dev: 'http://localhost:5179',
      prod: 'https://thefomlabs.com',
    },
    design: {
      fontFamily: "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontWeights: { normal: 400, medium: 500, semibold: 600, bold: 700 },
      radius: '0.5rem',
      shadows: {
        sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        md: '0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        lg: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      },
      googleFonts: { families: ['Inter:wght@400;500;600'], display: 'swap' },
    },
  },
  // Branding por defecto para "Accounts Central" (cuando app es desconocido o null)
  default: {
    appName: 'default',
    label: 'Accounts Central',
    colors: {
      primary: '210 100% 50%',             // #2383E2 (azul Notion)
      primaryForeground: '0 0% 100%',      // #ffffff
      background: '0 0% 100%',              // #ffffff
      foreground: '0 0% 22%',              // #37352F
      card: '0 0% 97%',                    // #F7F6F3
      cardForeground: '0 0% 22%',          // #37352F
      secondary: '0 0% 97%',               // #F7F6F3
      secondaryForeground: '0 0% 22%',    // #37352F
      muted: '0 0% 97%',                   // #F7F6F3
      mutedForeground: '0 0% 47%',         // #787774
      border: '0 0% 91%',                  // #E9E9E7
      input: '0 0% 91%',                   // #E9E9E7
      ring: '210 100% 50%',                // Mismo que primary
    },
    assets: {
      logo: '/branding/default/logo.svg',
      logoDark: '/branding/default/logo-dark.svg',
    },
    copy: {
      title: 'Iniciar sesión',
      subtitle: 'Accede a tu cuenta',
      buttonText: 'Iniciar sesión',
    },
    splash: {
      words: ['Cargando', 'Autenticando', 'Conectando', 'Verificando', 'Preparando'],
    },
    // Sin landingUrl en dev: apuntar el iframe a la misma app (p. ej. :5176 → :5176)
    // provoca recursión infinita (iframe dentro de iframe) y la consola no para.
    // Si en prod accounts.thefomlabs.com es solo marketing estático, el iframe es seguro.
    landingUrl: {
      prod: 'https://accounts.thefomlabs.com',
    },
    design: {
      // 🎨 ADN POR DEFECTO - Basado en shared/index.scss y accounts
      fontFamily: "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif", // Valor de accounts/src/styles/auth.css
      fontWeights: {
        normal: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
      },
      radius: '0.5rem',                    // 8px - Valor de shared/index.scss
      shadows: {
        sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        md: '0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        lg: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      },
      googleFonts: {
        families: ['Inter:wght@400;500;600'], // Font cargado en accounts/index.html
        display: 'swap',
      },
    },
  },
};

/**
 * Obtiene la configuración de branding para una app
 * 
 * @param appName - Nombre de la app (ej: 'empresa', 'tienda', 'houseme')
 * @returns Configuración de branding o branding por defecto (Accounts Central)
 */
export function getBrandingConfig(appName?: string | null): BrandingConfig {
  if (!appName) {
    return BRANDING_CONFIGS.default;
  }
  return BRANDING_CONFIGS[appName] || BRANDING_CONFIGS.default;
}

/**
 * Valida si una app tiene configuración de branding
 * 
 * @param appName - Nombre de la app
 * @returns true si la app tiene branding configurado
 */
export function hasBrandingConfig(appName: string): boolean {
  return appName in BRANDING_CONFIGS && appName !== 'default';
}
